import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/ui/lib/utils";
import { forwardRef } from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-3xl text-xl font-bold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300",
  {
    variants: {
      variant: {
        default:
          "border-[3px] dark:border-primary-white border-secondary-black bg-secondary-black text-primary-white hover:bg-slate-900/90 dark:bg-primary-white dark:text-primary-magenta dark:hover:bg-slate-50/90",
        "no-dark":
          "border-[3px] border-secondary-black bg-secondary-black text-primary-white hover:bg-slate-900/90",
        destructive:
          "bg-red-500 text-slate-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-slate-50 dark:hover:bg-red-900/90",
        outline:
          "border-[3px] border-secondary-black dark:border-primary-white text-secondary-black dark:text-primary-white bg-transparent hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-50",
        secondary:
          "bg-slate-100 text-slate-900 hover:bg-slate-100/80 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-800/80",
        tertiary:
          "bg-secondary-gray2 text-secondary-gray1 hover:bg-secondary-gray3 dark:opacity-50 dark:bg-primary-white dark:text-primary-black dark:hover:opacity-100",
        ghost: "",
        link: "font-medium text-slate-900 underline underline-offset-8 decoration-4 dark:text-slate-50",
        "full-width":
          "w-full text-sm border-[3px] dark:border-primary-white border-secondary-black bg-secondary-black text-primary-white hover:bg-slate-900/90 dark:bg-primary-white dark:text-primary-magenta dark:hover:bg-slate-50/90",
      },
      size: {
        default: "h-10 py-[20px] px-[40px]",
        slim: "h-[46px] p-0 px-[32px] decoration-1",
        sm: "h-9 text-[14px] border-[1.5px]",
        lg: "h-14 rounded-[20px] px-8",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
